import { motion } from 'motion/react';
import { Wrench, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/lib/external-links';

/**
 * MaintenancePage - Service maintenance page
 *
 * Display scenarios:
 * 1. Server unavailable (API health check failed)
 * 2. Network connection issues
 *
 * Features:
 * - Show friendly maintenance message
 * - Show auto-retry countdown
 * - Provide manual retry button
 */

export interface MaintenancePageProps {
  /** Error type */
  type?: 'maintenance' | 'network';
  /** Custom title */
  title?: string;
  /** Custom description */
  description?: string;
  /** Seconds until next auto-retry (0 means no auto-retry) */
  retryIn?: number;
  /** Whether retrying is in progress */
  isRetrying?: boolean;
  /** Manual retry callback */
  onRetry?: () => void;
}

export function MaintenancePage({
  type = 'maintenance',
  title,
  description,
  retryIn = 0,
  isRetrying = false,
  onRetry,
}: MaintenancePageProps) {
  const isMaintenance = type === 'maintenance';

  const defaultTitle = isMaintenance ? 'Service Upgrade in Progress' : 'Network Connection Issue';
  const defaultDescription = isMaintenance
    ? 'We are performing system maintenance. Please try again later.'
    : 'Unable to connect to server. Please check your network and try again.';

  const Icon = isMaintenance ? Wrench : WifiOff;
  const iconBgColor = isMaintenance
    ? 'from-amber-400 to-orange-500'
    : 'from-slate-400 to-slate-600';
  const iconShadowColor = isMaintenance
    ? 'shadow-amber-500/30'
    : 'shadow-slate-500/30';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center"
      >
        {/* Icon */}
        <motion.div
          className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br ${iconBgColor} shadow-lg ${iconShadowColor}`}
          animate={{
            scale: [1, 1.05, 1],
            rotate: isMaintenance ? [0, 5, -5, 0] : 0,
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <Icon className="h-10 w-10 text-white" />
        </motion.div>

        {/* Title */}
        <h1 className="mb-3 text-2xl font-bold text-slate-800">
          {title || defaultTitle}
        </h1>

        {/* Description */}
        <p className="mb-6 text-slate-600">{description || defaultDescription}</p>

        {/* Retry Status */}
        {retryIn > 0 && !isRetrying && (
          <motion.div
            className="mb-6 flex items-center justify-center gap-2 text-sm text-slate-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Wifi className="h-4 w-4" />
            <span>Auto-retry in {retryIn} seconds...</span>
          </motion.div>
        )}

        {isRetrying && (
          <motion.div
            className="mb-6 flex items-center justify-center gap-2 text-sm text-indigo-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <RefreshCw className="h-4 w-4" />
            </motion.div>
            <span>Checking service status...</span>
          </motion.div>
        )}

        {/* Retry Button */}
        {onRetry && (
          <Button
            onClick={onRetry}
            disabled={isRetrying}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700"
            size="lg"
          >
            {isRetrying ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                </motion.div>
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry Now
              </>
            )}
          </Button>
        )}

        {/* Help Text */}
        <p className="mt-6 text-xs text-slate-400">
          If the problem persists, please contact{' '}
          <a
            href={SUPPORT_MAILTO}
            className="text-indigo-500 hover:text-indigo-600 hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
      </motion.div>
    </div>
  );
}

